/**
 * Admin · regions & auto-posting (GET/PUT /admin/regions).
 * Per-region job-board mappings are live + editable (enable toggle, priority, add board);
 * residency/comms/payments/KYC/regime are the region's static config card.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BoardMapping, Region } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Info, ShieldCheck } from "../../components/icons.js";
import { Switch, apiErrorText } from "./adminUi.js";

interface MappingsResponse {
  mappings: BoardMapping[];
}

interface RegionMeta {
  region: Region;
  flag: string;
  label: string;
  pill: { cls: string; text: string };
  kv: [string, string][];
  note: { kind: "info" | "warn"; text: ReactNode };
}

const REGIONS: RegionMeta[] = [
  {
    region: "in-central",
    flag: "🇮🇳",
    label: "India",
    pill: { cls: "pill v", text: "Live" },
    kv: [
      ["Data residency", "Central India (Pune)"],
      // Only email actually sends today — no WhatsApp/SMS BSP is wired yet (honest copy).
      ["Comms", "Email (live) · WhatsApp/SMS (integration pending)"],
      // createRazorpayOrder hardcodes INR — INR is the only settlement currency wired. No USD rail exists.
      ["Payments", "Razorpay · INR"],
      // No Aadhaar/DigiLocker provider exists yet — the KYC adapter defaults to 'sim'.
      ["KYC", "Simulated (vendor integration pending)"],
      ["Compliance", "DPDP Act 2023"],
    ],
    note: {
      kind: "warn",
      text: (
        <>
          KYC is <b>simulated</b> today — no Aadhaar/DigiLocker provider is wired. A verified vendor
          integration lands before any real ID check runs.
        </>
      ),
    },
  },
  {
    region: "us-east",
    flag: "🇺🇸",
    label: "USA",
    pill: { cls: "pill pv", text: "Provisioning" },
    kv: [
      ["Data residency", "East US (Virginia)"],
      ["Comms", "Email + SMS"],
      ["Payments", "Stripe · USD (planned — not implemented)"],
      ["KYC", "Document + selfie liveness"],
      ["Compliance", "EEOC · NYC LL144"],
    ],
    note: { kind: "info", text: <>LL144 bias-audit export wired in before go-live — see Compliance.</> },
  },
  {
    region: "eu-west",
    flag: "🇪🇺",
    label: "European Union",
    pill: { cls: "pill n", text: "Planned" },
    kv: [
      ["Data residency", "West Europe (Amsterdam)"],
      ["Comms", "Email + SMS · locale-aware"],
      ["Payments", "Stripe · EUR / SEPA (planned — not implemented)"],
      ["KYC", "eIDAS / document liveness"],
      ["Compliance", "GDPR + EU AI Act"],
    ],
    note: {
      kind: "warn",
      text: (
        <>
          EU AI Act: interview scoring is high-risk — the human-decision gate is <b>mandatory</b>, already core to
          the engine.
        </>
      ),
    },
  },
];

export function Regions(): ReactNode {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-regions"],
    queryFn: () => api.get<MappingsResponse>("/admin/regions"),
  });
  const mappings = data?.mappings ?? [];

  const [newBoard, setNewBoard] = useState<Record<string, string>>({});

  const upsert = useMutation({
    mutationFn: (input: { region: Region; board: string; enabled: boolean; priority: number }) =>
      api.put<{ mapping: BoardMapping }>("/admin/regions", input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-regions"] }),
  });

  const forRegion = (region: Region): BoardMapping[] =>
    mappings
      // Worker posts `ORDER BY priority DESC` — render highest-priority (posts first) at the top.
      .filter((m) => m.region === region)
      .sort((a, b) => b.priority - a.priority || a.board.localeCompare(b.board));

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Regions & auto-posting</h1>
          <div className="sub">Where data lives, which boards jobs auto-post to, and how candidates are reached</div>
        </div>
        <span className="pill n">Config module, not a rebuild</span>
      </div>

      <div className="banner info" style={{ marginBottom: 18 }}>
        <Info />
        <div>
          Each region is a <b>self-contained config</b>: job-board mapping, data residency, comms channels, payment
          rails, KYC method and compliance regime. Launching a new country means filling one of these cards — not
          re-architecting the platform.
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading board mappings…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load board mappings. Please retry in a moment.</span>
        </div>
      )}

      {upsert.isError && (
        <div className="banner crit" style={{ marginBottom: 16 }}>
          <span>{apiErrorText(upsert.error, "Couldn't save the mapping. Please retry.")}</span>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="grid g3" style={{ marginBottom: 16 }}>
          {REGIONS.map((r) => {
            const rows = forRegion(r.region);
            const draft = newBoard[r.region] ?? "";
            return (
              <div key={r.region} className="card">
                <div className="card-h">
                  <h3>
                    {r.flag} {r.label}
                  </h3>
                  <span className={r.pill.cls}>{r.pill.text}</span>
                </div>
                <div className="card-b">
                  <div className="tag" style={{ marginBottom: 8 }}>
                    Auto-post job boards · Simulated — no board integration is live
                  </div>
                  {rows.length === 0 && (
                    <p className="mut sm" style={{ marginTop: 0 }}>
                      No boards mapped yet.
                    </p>
                  )}
                  {rows.map((m) => (
                    <div
                      key={m.id}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0" }}
                    >
                      <span style={{ flex: 1, fontWeight: 600, textTransform: "capitalize" }}>
                        {m.board.replace(/_/g, " ")}
                      </span>
                      <span className="mut sm tnum" title="Priority (higher posts first)">
                        p{m.priority}
                      </span>
                      <button
                        className="btn sm ghost"
                        aria-label="Higher priority — posts first"
                        data-tip="Higher priority"
                        disabled={upsert.isPending}
                        onClick={() =>
                          upsert.mutate({
                            region: m.region,
                            board: m.board,
                            enabled: m.enabled,
                            priority: m.priority + 1,
                          })
                        }
                      >
                        ↑
                      </button>
                      <button
                        className="btn sm ghost"
                        aria-label="Lower priority — posts later"
                        data-tip="Lower priority"
                        disabled={m.priority === 0 || upsert.isPending}
                        onClick={() =>
                          upsert.mutate({
                            region: m.region,
                            board: m.board,
                            enabled: m.enabled,
                            priority: Math.max(0, m.priority - 1),
                          })
                        }
                      >
                        ↓
                      </button>
                      <Switch
                        on={m.enabled}
                        disabled={upsert.isPending}
                        onToggle={() =>
                          upsert.mutate({
                            region: m.region,
                            board: m.board,
                            enabled: !m.enabled,
                            priority: m.priority,
                          })
                        }
                      />
                    </div>
                  ))}

                  <div style={{ display: "flex", gap: 8, marginTop: 8, marginBottom: 14 }}>
                    <input
                      className="input"
                      placeholder="Add board (e.g. indeed)"
                      value={draft}
                      onChange={(e) => setNewBoard((s) => ({ ...s, [r.region]: e.target.value }))}
                    />
                    <button
                      className="btn sm"
                      disabled={draft.trim().length < 2 || upsert.isPending}
                      onClick={() => {
                        upsert.mutate({
                          region: r.region,
                          board: draft.trim().toLowerCase(),
                          enabled: true,
                          priority: rows.length,
                        });
                        setNewBoard((s) => ({ ...s, [r.region]: "" }));
                      }}
                    >
                      Add
                    </button>
                  </div>

                  {r.kv.map(([k, v]) => (
                    <div key={k} className="krow">
                      <span className="k">{k}</span>
                      <span className="v sm">{v}</span>
                    </div>
                  ))}

                  <div className={`banner ${r.note.kind}`} style={{ marginTop: 12 }}>
                    <ShieldCheck />
                    <div>{r.note.text}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="disclaimer">
        <b>Region config drives display and routing only.</b> This is a single-cell deployment in Central India —
        one Postgres serves every region today; per-region data residency is on the roadmap, not yet enforced.
        Board auto-posting is simulated (no live integration). Mapping changes are written to the audit log.
      </div>
    </section>
  );
}
